<template>
  <div class="progress-line-container">
    <span
      class="progress-line-fill"
      :style="progressStyle"
      >
      <span class="progress-line-round">
        <span v-if="label" class="progress-line-label">{{ label }}</span>
      </span>
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';

const props = defineProps<{
  width: number;
  label?: string;
}>();

const windowWidth = ref(window.innerWidth);

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

const progressStyle = computed(() => {
  const size = `${props.width - 1}%`;
  return {
    display: props.width ? 'block' : 'none',
    ...(windowWidth.value >= 1280 ? { width: size } : { height: size }),
  };
});
</script>

<style scoped>
.progress-line-container {
  position: relative;
  width: 0.375rem;
  background: rgba(108, 189, 69, 0.1);
  border-radius: 5px;
}

.progress-line-fill {
  position: relative;
  display: block;
  width: 100%;
  background: var(--ch-blue-100);
  border-radius: 5px;
  opacity: 0;
  animation: progressFade 0.3s ease-in-out forwards;
  -webkit-animation: progressFade 0.3s ease-in-out forwards;
}

.progress-line-round {
  position: absolute;
  display: block;
  width: 1rem;
  height: 1rem;
  top: 99%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--ch-blue-100);
  border: 4px solid #dae1e9;
  border-radius: 100%;
}

.progress-line-label {
  max-width: 4.5rem;
  min-width: 4rem;
  position: absolute;
  rotate: -90deg;
  top: -0.5rem;
  left: -3.25rem;
  font-size: var(--font-xs);
  font-weight: 500;
  text-align: center;
  text-transform: capitalize;
  background: var(--ch-blue-100);
  color: var(--ch-white);
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
  z-index: 1;
}

.progress-line-label::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -0.125rem;
  width: 0.625rem;
  height: 0.5rem;
  background: var(--ch-blue-100);
  transform: translateX(-50%) rotate(45deg);
  z-index: -1;
}

@keyframes progressFade {
  0% {
    width: 0;
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@media screen and (min-width: 1280px) {
  .progress-line-container {
    width: 100%;
    height: 0.375rem;
  }

  .progress-line-fill {
    height: 100%;
  }

  .progress-line-round {
    top: 50%;
    left: 99.5%;
    transform: translateY(-50%);
  }

  .progress-line-label {
    rotate: 0deg;
    top: -2.5rem;
    left: calc(100% - 5px);
    transform: translateX(-50%);
  }
}
</style>